import { api } from '@packages/backend/convex/_generated/api';
import { formatCents, getUserErrorMessage, type RegisterClosureCvx } from '@packages/shared';
import { createFileRoute } from '@tanstack/react-router';
import { useConvex } from 'convex/react';
import { useQuery } from 'convex-helpers/react';
import { format } from 'date-fns';
import { Download } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import { ActionButton } from '@/components/ActionButton';
import { ActionDropdown } from '@/components/ActionDropdown';
import { ColorBadge } from '@/components/ColorBadge';
import { ErrorAlert } from '@/components/ErrorAlert';
import { LoadingIcon } from '@/components/LoadingIcon';
import { PriceInput } from '@/components/PriceInput';
import { Card, CardAction, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { SuperAdminOnly } from '@/features/auth/SuperAdminOnly';

export const Route = createFileRoute('/r/$restaurantId/register-closures')({
  component: RegisterClosuresPage,
});

/**
 * Unattended closures are recorded with nobody at the till, so their actuals stay null until
 * entered here.
 */
const isMissingActuals = (r: RegisterClosureCvx): boolean =>
  r.type === 'UNATTENDED' && r.cashActual === null && r.eftposActual === null;

function RegisterClosuresPage() {
  const exportToExcel = (registerClosures: RegisterClosureCvx[]) => {
    const formattedRegisterClosures = registerClosures.map((r) => ({
      Date: format(r.closedAt, 'yyyy-MM-dd'),
      Time: format(r.closedAt, 'HH:mm'),
      Type: r.type === 'UNATTENDED' ? 'Unattended' : 'Manual',
      CashExpected: formatCents(r.cashExpected),
      CashActual: r.cashActual === null ? '' : formatCents(r.cashActual),
      CashDiff: r.cashDiff === null ? '' : formatCents(r.cashDiff),
      EftposExpected: formatCents(r.eftposExpected),
      EftposActual: r.eftposActual === null ? '' : formatCents(r.eftposActual),
      EftposDiff: r.eftposDiff === null ? '' : formatCents(r.eftposDiff),
    }));

    const worksheet = XLSX.utils.json_to_sheet(formattedRegisterClosures);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Register Closures');
    XLSX.writeFile(workbook, 'register-closures.xlsx');
  };

  const { restaurantId } = Route.useParams();
  const {
    data: registerClosures,
    error,
    isError,
    isPending,
  } = useQuery(api.registerClosures.listByRestaurantId, { restaurantId });

  if (isError) {
    return <ErrorAlert message={error.message} title="Error loading register closures" />;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Register closures</CardTitle>
        <CardAction>
          {!isPending && registerClosures && registerClosures.length > 0 && (
            <ActionButton
              onClick={() => exportToExcel(registerClosures)}
              variant="outline"
              size="sm"
            >
              <Download className="h-4 w-4" />
              Excel File
            </ActionButton>
          )}
        </CardAction>
      </CardHeader>
      <CardContent>
        {isPending ? (
          <LoadingIcon />
        ) : (
          <RegisterClosuresTable registerClosures={registerClosures} />
        )}
      </CardContent>
    </Card>
  );
}

const RegisterClosuresTable = ({
  registerClosures,
}: {
  registerClosures: RegisterClosureCvx[];
}) => {
  const convex = useConvex();
  const [closureForActuals, setClosureForActuals] = useState<RegisterClosureCvx | null>(null);

  const handleDelete = async (registerClosureId: string) => {
    try {
      await convex.mutation(api.registerClosures.deleteRegisterClosure, { registerClosureId });
      toast.success('Deleted successfully');
    } catch (error) {
      toast.error("Couldn't delete", {
        description: getUserErrorMessage(error),
      });
    }
  };

  return (
    <>
      <EnterActualsDialog
        registerClosure={closureForActuals}
        onClose={() => setClosureForActuals(null)}
      />
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Date</TableHead>
            <TableHead>Time</TableHead>
            <TableHead>Type</TableHead>
            <TableHead className="text-right">Cash expected</TableHead>
            <TableHead className="text-right">Cash actual</TableHead>
            <TableHead className="text-right">Cash difference</TableHead>

            <TableHead className="text-right">EFTPOS expected</TableHead>
            <TableHead className="text-right">EFTPOS actual</TableHead>
            <TableHead className="text-right">EFTPOS difference</TableHead>
            <SuperAdminOnly>
              <TableHead />
            </SuperAdminOnly>
          </TableRow>
        </TableHeader>
        <TableBody>
          {registerClosures.map((r) => {
            return (
              <TableRow key={r._id}>
                <TableCell className="py-0 whitespace-nowrap">
                  <div className="font-medium">{format(r.closedAt, 'dd-MMM-yy, EEE')}</div>
                </TableCell>

                <TableCell className="py-0 whitespace-nowrap">
                  <div className="font-medium">{format(r.closedAt, 'h:mm a')}</div>
                </TableCell>

                {/* Closure type — badge un-reconciled unattended days so owners can see them. */}
                <TableCell className="py-0 whitespace-nowrap">
                  {r.type === 'UNATTENDED' ? (
                    <div className="flex items-center gap-2">
                      <ColorBadge color="purple">Unattended</ColorBadge>
                      {isMissingActuals(r) && (
                        <ActionButton
                          variant="outline"
                          size="sm"
                          onClick={() => setClosureForActuals(r)}
                        >
                          Add amounts
                        </ActionButton>
                      )}
                    </div>
                  ) : (
                    'Manual'
                  )}
                </TableCell>

                {/* Cash */}
                <TableCell className="py-0 text-right">
                  {r.cashExpected === 0 ? '-' : formatCents(r.cashExpected, '$')}
                </TableCell>
                <TableCell className="py-0 text-right">
                  {r.cashActual === null || r.cashActual === 0
                    ? '-'
                    : formatCents(r.cashActual, '$')}
                </TableCell>
                <TableCell className="py-0 bg-muted/60 text-right">
                  <DiffCell diff={r.cashDiff} />
                </TableCell>

                {/* EFTPOS */}
                <TableCell className="py-0 text-right">
                  {r.eftposExpected === 0 ? '-' : formatCents(r.eftposExpected, '$')}
                </TableCell>
                <TableCell className="py-0 text-right">
                  {r.eftposActual === null || r.eftposActual === 0
                    ? '-'
                    : formatCents(r.eftposActual, '$')}
                </TableCell>
                <TableCell className="font-medium py-0 bg-muted/60 text-right">
                  <DiffCell diff={r.eftposDiff} />
                </TableCell>
                <SuperAdminOnly>
                  <TableCell className="py-0">
                    <ActionDropdown onDelete={() => handleDelete(r._id)} />
                  </TableCell>
                </SuperAdminOnly>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </>
  );
};

const DiffCell = ({ diff }: { diff: number | null }) => {
  if (diff === null) return <>-</>;
  if (diff === 0) return <span className="text-green-600">balanced</span>;
  return <>{diff > 0 ? `+${formatCents(diff, '$')}` : formatCents(diff, '$')}</>;
};

/**
 * Enter counted cash/EFTPOS on an unattended closure. Short/over recomputes server-side; the
 * closure stays UNATTENDED and amounts can only be entered once.
 */
const EnterActualsDialog = ({
  registerClosure,
  onClose,
}: {
  registerClosure: RegisterClosureCvx | null;
  onClose: () => void;
}) => {
  const convex = useConvex();
  const [cashActual, setCashActual] = useState<number | null>(null);
  const [eftposActual, setEftposActual] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Actuals can only be entered once server-side, so a cancelled entry must never leak into
  // the next closure's dialog with Save already enabled.
  const handleClose = () => {
    setCashActual(null);
    setEftposActual(null);
    onClose();
  };

  const handleSave = async () => {
    if (!registerClosure || cashActual === null || eftposActual === null) return;
    setIsSaving(true);
    try {
      await convex.mutation(api.registerClosures.enterRegisterClosureActuals, {
        registerClosureId: registerClosure._id,
        cashActual,
        eftposActual,
      });
      toast.success('Amounts saved');
      handleClose();
    } catch (error) {
      toast.error("Couldn't save amounts", { description: getUserErrorMessage(error) });
    }
    setIsSaving(false);
  };

  return (
    <Dialog open={registerClosure !== null} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add the counted amounts?</DialogTitle>
          <DialogDescription>
            The register closed itself
            {registerClosure ? ` on ${format(registerClosure.closedAt, 'dd MMM')}` : ''}, so nobody
            counted the till. Add what was counted to see the short/over.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-2">
          <div className="grid gap-2">
            <Label htmlFor="cash-actual">
              Cash counted (expected{' '}
              {registerClosure ? formatCents(registerClosure.cashExpected, '$') : '-'})
            </Label>
            <PriceInput id="cash-actual" value={cashActual} onChange={setCashActual} />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="eftpos-actual">
              EFTPOS total (expected{' '}
              {registerClosure ? formatCents(registerClosure.eftposExpected, '$') : '-'})
            </Label>
            <PriceInput id="eftpos-actual" value={eftposActual} onChange={setEftposActual} />
          </div>
        </div>
        <DialogFooter>
          <ActionButton variant="outline" onClick={handleClose}>
            Cancel
          </ActionButton>
          <ActionButton
            onClick={handleSave}
            disabled={cashActual === null || eftposActual === null || isSaving}
          >
            Save amounts
          </ActionButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
